/**
 * Upsell Orphan Remover
 *
 * Automatically removes upsell items from the cart when their parent product
 * is removed. Matches upsell items by their "For Product" line item property.
 */
(function() {
  'use strict';

  // Debounce to prevent multiple rapid calls
  let isProcessing = false;
  let pendingCheck = false;

  /**
   * Get checkout buttons in cart drawer and cart page
   */
  function getCheckoutButtons() {
    return document.querySelectorAll('.cart-drawer .checkout-buttons a, .cart-drawer .checkout-buttons button, .cart-page .checkout-buttons a, .cart-page .checkout-buttons button');
  }

  /**
   * Disable checkout buttons while processing
   */
  function setCheckoutDisabled(disabled) {
    const buttons = getCheckoutButtons();
    buttons.forEach(btn => {
      if (disabled) {
        btn.setAttribute('data-orphan-disabled', 'true');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.5';
      } else {
        // Only re-enable if we were the ones who disabled it
        if (btn.getAttribute('data-orphan-disabled') === 'true') {
          btn.removeAttribute('data-orphan-disabled');
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        }
      }
    });
  }

  /**
   * Fetch the current cart data
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return await response.json();
    } catch (e) {
      console.warn('Upsell orphan remover: Failed to fetch cart', e);
      return null;
    }
  }

  /**
   * Remove items from cart by their line item keys
   */
  async function removeItems(keys) {
    if (!keys || keys.length === 0) return false;

    const updates = {};
    keys.forEach(key => {
      updates[key] = 0;
    });

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates: updates })
      });

      if (response.ok) {
        // Trigger cart refresh event so the UI updates
        document.dispatchEvent(new CustomEvent('theme:cartchanged'));
        return true;
      }
    } catch (e) {
      console.warn('Upsell orphan remover: Failed to remove items', e);
    }

    return false;
  }

  /**
   * Check for orphaned upsells and remove them
   */
  async function checkAndRemoveOrphans() {
    // Prevent concurrent processing
    if (isProcessing) {
      pendingCheck = true;
      return;
    }

    isProcessing = true;
    let hasOrphans = false;

    try {
      const cart = await getCart();
      if (!cart || !cart.items || cart.items.length === 0) {
        return;
      }

      // Build a set of all product titles currently in cart (that are NOT upsells)
      const parentTitles = new Set();
      const orphanedKeys = [];

      // First pass: identify parent products (items without "For Product" property)
      cart.items.forEach(item => {
        const forProduct = item.properties && item.properties['For Product'];
        if (!forProduct) {
          // This is a parent/regular product, add its title
          parentTitles.add(item.product_title);
        }
      });

      // Second pass: find orphaned upsells
      cart.items.forEach(item => {
        const forProduct = item.properties && item.properties['For Product'];
        if (forProduct) {
          // This is an upsell - check if its parent still exists
          if (!parentTitles.has(forProduct)) {
            // Parent is gone, mark this upsell for removal
            orphanedKeys.push(item.key);
            console.log('Upsell orphan remover: Removing orphaned upsell for "' + forProduct + '"');
          }
        }
      });

      // Remove orphaned upsells
      if (orphanedKeys.length > 0) {
        hasOrphans = true;
        // Disable checkout while removing
        setCheckoutDisabled(true);
        await removeItems(orphanedKeys);
      }
    } catch (e) {
      console.warn('Upsell orphan remover: Error checking orphans', e);
    } finally {
      // Always re-enable checkout and reset processing flag
      if (hasOrphans) {
        setCheckoutDisabled(false);
      }
      isProcessing = false;
    }

    // If another check was requested while processing, run it now
    if (pendingCheck) {
      pendingCheck = false;
      // Small delay to let the cart settle
      setTimeout(checkAndRemoveOrphans, 300);
    }
  }

  /**
   * Initialize listeners
   */
  function init() {
    // Listen for cart changes
    document.addEventListener('theme:cartchanged', function() {
      // Small delay to ensure cart is updated
      setTimeout(checkAndRemoveOrphans, 100);
    });

    // Also listen for the other cart event used in this theme
    document.addEventListener('cc:cart-updated', function() {
      setTimeout(checkAndRemoveOrphans, 100);
    });

    // Run initial check on page load (in case page was refreshed with orphans)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(checkAndRemoveOrphans, 500);
      });
    } else {
      setTimeout(checkAndRemoveOrphans, 500);
    }
  }

  init();

})();
